import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "./api";
import { Btn, Field, TextArea, TextInput, Toggle, useSaveState } from "./atoms";

/* Body editor — block list with inline editing.
   Supported types: p, h2, h3, ul, ol, image, pull, cta */

const newBlock = (type) => {
  switch (type) {
    case "h2":
    case "h3":
    case "p":
    case "pull":
      return { type, text: "" };
    case "ul":
    case "ol":
      return { type, items: [""] };
    case "image":
      return { type, src: "", alt: "", caption: "" };
    case "cta":
      return { type, text: "Read more", href: "#" };
    default:
      return { type: "p", text: "" };
  }
};

const BlockEditor = ({ block, onChange, onRemove, onMoveUp, onMoveDown, idx, total }) => {
  const set = (k, v) => onChange({ ...block, [k]: v });
  return (
    <div className="bg-[#0B0F14] border border-white/[0.06] rounded-md p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
          {block.type}
        </span>
        <div className="flex items-center gap-1">
          <Btn variant="ghost" onClick={onMoveUp} disabled={idx === 0} className="!px-2 !py-1">↑</Btn>
          <Btn variant="ghost" onClick={onMoveDown} disabled={idx === total - 1} className="!px-2 !py-1">↓</Btn>
          <Btn variant="danger" onClick={onRemove} className="!px-2 !py-1">×</Btn>
        </div>
      </div>
      {block.type === "p" || block.type === "pull" ? (
        <TextArea value={block.text} onChange={(v) => set("text", v)} rows={block.type === "pull" ? 2 : 4} />
      ) : null}
      {block.type === "h2" || block.type === "h3" ? (
        <TextInput value={block.text} onChange={(v) => set("text", v)} />
      ) : null}
      {block.type === "ul" || block.type === "ol" ? (
        <div className="space-y-2">
          {block.items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <TextInput
                value={it}
                onChange={(v) => {
                  const items = [...block.items];
                  items[i] = v;
                  set("items", items);
                }}
              />
              <Btn variant="danger" className="!px-2 !py-1" onClick={() => set("items", block.items.filter((_, j) => j !== i))}>×</Btn>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => set("items", [...block.items, ""])}>＋ Add item</Btn>
        </div>
      ) : null}
      {block.type === "image" ? (
        <div className="space-y-3">
          <TextInput value={block.src} onChange={(v) => set("src", v)} placeholder="Image URL" />
          <TextInput value={block.alt} onChange={(v) => set("alt", v)} placeholder="Alt text" />
          <TextInput value={block.caption} onChange={(v) => set("caption", v)} placeholder="Caption (optional)" />
          {block.src ? <img src={block.src} alt="" className="rounded max-h-40 border border-white/10" /> : null}
        </div>
      ) : null}
      {block.type === "cta" ? (
        <div className="grid grid-cols-2 gap-3">
          <TextInput value={block.text} onChange={(v) => set("text", v)} placeholder="Label" />
          <TextInput value={block.href} onChange={(v) => set("href", v)} placeholder="URL" />
        </div>
      ) : null}
    </div>
  );
};

export default function BlogEditPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const { saving, saved, error, wrap } = useSaveState();

  useEffect(() => {
    api.blogGet(slug).then(setPost).catch(() => setPost(false));
  }, [slug]);

  if (post === null) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (post === false) return <div className="text-sm text-red-400">Post not found.</div>;

  const set = (k, v) => setPost({ ...post, [k]: v });
  const setSeo = (k, v) => setPost({ ...post, seo: { ...(post.seo || {}), [k]: v } });

  const save = () =>
    wrap(async () => {
      const slugChanged = post.slug !== slug;
      await api.blogUpdate(slug, post);
      if (slugChanged) nav(`/admin/blog/${post.slug}`, { replace: true });
    });

  const setBody = (body) => set("body", body);
  const addBlock = (type) => setBody([...(post.body || []), newBlock(type)]);
  const updateBlock = (i, b) =>
    setBody(post.body.map((x, j) => (j === i ? b : x)));
  const removeBlock = (i) => setBody(post.body.filter((_, j) => j !== i));
  const moveBlock = (i, dir) => {
    const arr = [...post.body];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBody(arr);
  };

  return (
    <div data-testid="admin-blog-edit" className="pb-20">
      <div className="flex items-center justify-between gap-4 mb-9">
        <div>
          <Link to="/admin/blog" className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500 hover:text-[#F4D64E]">
            ← Blog
          </Link>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em] truncate">
            {post.title || "Untitled"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {saved ? <span className="text-[0.7rem] text-[#F4D64E]">Saved ✓</span> : null}
          {error ? <span className="text-[0.7rem] text-red-400">{error}</span> : null}
          <a
            href={post.published ? `/blog/${post.slug}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-neutral-400 hover:text-white"
          >
            View →
          </a>
          <Btn onClick={save} disabled={saving} data-testid="blog-save">
            {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-7">
          <Field label="Title">
            <TextInput value={post.title} onChange={(v) => set("title", v)} />
          </Field>
          <Field label="Slug" hint={`/blog/${post.slug}`}>
            <TextInput value={post.slug} onChange={(v) => set("slug", v)} />
          </Field>
          <Field label="Excerpt">
            <TextArea value={post.excerpt} onChange={(v) => set("excerpt", v)} rows={3} />
          </Field>
          <Field label="Cover image (URL)">
            <TextInput value={post.coverImg} onChange={(v) => set("coverImg", v)} />
            {post.coverImg ? (
              <img src={post.coverImg} alt="" className="mt-3 rounded max-h-48 border border-white/10" />
            ) : null}
          </Field>

          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Body</span>
              <div className="flex flex-wrap gap-1">
                {["h2", "h3", "p", "ul", "ol", "image", "pull", "cta"].map((t) => (
                  <Btn key={t} variant="ghost" className="!px-2 !py-1" onClick={() => addBlock(t)}>
                    + {t}
                  </Btn>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {(post.body || []).map((b, i) => (
                <BlockEditor
                  key={i}
                  idx={i}
                  total={post.body.length}
                  block={b}
                  onChange={(nb) => updateBlock(i, nb)}
                  onRemove={() => removeBlock(i)}
                  onMoveUp={() => moveBlock(i, -1)}
                  onMoveDown={() => moveBlock(i, 1)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-7">
          <div className="space-y-3">
            <Toggle value={post.published} onChange={(v) => set("published", v)} label={post.published ? "Live" : "Draft"} />
            <Toggle value={!!post.featured} onChange={(v) => set("featured", v)} label="Featured" />
          </div>
          <Field label="Number / Index"><TextInput value={post.n} onChange={(v) => set("n", v)} placeholder="01, 02, …" /></Field>
          <Field label="Category"><TextInput value={post.category} onChange={(v) => set("category", v)} /></Field>
          <Field label="Read time"><TextInput value={post.readTime} onChange={(v) => set("readTime", v)} /></Field>
          <Field label="Date / Tag"><TextInput value={post.date} onChange={(v) => set("date", v)} /></Field>

          <div className="pt-6 border-t border-white/[0.06]">
            <div className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-4">SEO</div>
            <div className="space-y-4">
              <Field label="Meta title"><TextInput value={post.seo?.title} onChange={(v) => setSeo("title", v)} /></Field>
              <Field label="Meta description"><TextArea value={post.seo?.description} onChange={(v) => setSeo("description", v)} rows={3} /></Field>
              <Field label="OG image URL"><TextInput value={post.seo?.og_image} onChange={(v) => setSeo("og_image", v)} /></Field>
              <Field label="Canonical URL"><TextInput value={post.seo?.canonical} onChange={(v) => setSeo("canonical", v)} /></Field>
              <Toggle value={!!post.seo?.noindex} onChange={(v) => setSeo("noindex", v)} label="Noindex" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
